






































































































































































import { Vue, Component, Ref } from "vue-property-decorator";
import UserItemComponent from "./UserItem.vue";
import UserItemNonClickableComponent from "./UserItemNonClickable.vue";
import { UserModule } from "@/store/modules/UserModule";

import UserFormComponent from "@/components/users/UserFormDialog.vue";
import { getLicenceInfoOfMemberClients, observeLicenceInfoForClient } from "@/data/repository/LicenceRepository";
import { LicenceInfoV3 } from "@/data/models/LicenceInfoV3";
import LicenceUsageDetailsComponent from "../licences/LicenceUsageDetails.vue";
import User, { doesUserBelongToBranch, Role } from "@/data/models/User";
import { 
  adminRightsAccess, 
  createUserAccess, 
  editAdminAccess, 
  editUsersAccess 
} from "@/permissions/UserPermissions";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { getBranchFiltersOfUser } from "@/data/repository/UserRepository";
import { COLOR_LIST_ROW_HOVER_DARK, COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { themed } from "@/utils/Theme";
import { licenceAdministrationAccess } from "@/permissions/ClientPermissions";
import { getClientId, getMemberClientIds, getMemberClientsInformationOfGroup } from "@/data/repository/ClientRepository";
import MenuIconWithSelectionListComponent from "@/components/common/FilterMenu.vue";
import BranchFilterSelectorComponent from "@/components/common/BranchFilterSelector.vue";
import { BranchFilter } from "@/data/models/BranchFilter";
import { getRoleActive, getRoleLabel } from "@/data/models/RoleSettings";
import { getBranchName } from "@/data/models/Branch";
import ToolbarOverflowItemComponent from "@/components/common/ToolbarOverflowItem.vue";
import {
  getLastVisitedItem,
  LAST_VISITED_ITEM_BACKGROUND_STYLE,
  setLastVisitedItem,
  StorageKeyLastVisitedItemLevel,
} from "@/utils/Utils";
import { getEffectiveUser } from "@/utils/ViewAs";

enum UserSortOrder {
  NICKNAME = "NICKNAME",
  FIRST_NAME = "FIRST_NAME",
  LAST_NAME = "LAST_NAME",
}

const ROLE_FILTER_ALL = "ROLE_FILTER_ALL";

/** Last-visited key of a user row, prefixed so it never matches another list's entry. */
function userListLastVisitedKey(userId: string) {
  return `USERS/${userId}`;
}

@Component({
  components: {
    "toolbar-overflow-item": ToolbarOverflowItemComponent,
    "user-item": UserItemComponent,
    "user-item-non-clickable": UserItemNonClickableComponent,
    "user-form": UserFormComponent,
    "licence-usage-details": LicenceUsageDetailsComponent,
    "menu-icon": MenuIconWithSelectionListComponent,
    "branch-filter-selector": BranchFilterSelectorComponent,
  },
})
export default class UserManagementComponent extends Vue {
  @Ref("userForm")
  userFormDialog!: UserFormComponent;

  @Ref("licenceUsageDetails")
  licenceUsageDetails!: LicenceUsageDetailsComponent;

  @Ref("searchFieldDesktop") readonly searchFieldDesktop!: Vue;
  @Ref("searchFieldMobile") readonly searchFieldMobile!: Vue;

  async onCreateNewUser() {
    await this.userFormDialog.show();
  }

  subscriptions: Array<() => void> = [];

  licenceInfoV3: LicenceInfoV3 | null = null;
  licenceInfoV3OfGroup: LicenceInfoV3[] = [];

  numberOfMemberClientsForLicenceInfoV3 = 0;

  searchQuery = "";
  isSearchInputReadonly = true;
  searchTimeout: NodeJS.Timeout | null = null;

  selectedBranchFilter: BranchFilter = new BranchFilter(false);

  /** The user opened last, kept across visits so the row stays marked (ticket 2610031). */
  lastVisitedKey: string | null = getLastVisitedItem(StorageKeyLastVisitedItemLevel.LEVEL_1);

  selectedSortOrder = UserSortOrder.NICKNAME;
  sortOrders = [
    UserSortOrder.NICKNAME,
    UserSortOrder.FIRST_NAME,
    UserSortOrder.LAST_NAME,
  ];

  selectedRoleFilter: Role | typeof ROLE_FILTER_ALL = ROLE_FILTER_ALL;
  get roleFilters(): Array<Role | typeof ROLE_FILTER_ALL> {
    const activeRoles = Object.values(Role).filter((role) =>
      getRoleActive(role, this)
    );
    return [ROLE_FILTER_ALL, ...activeRoles];
  }

  /** The user whose view this shows: the chosen one while "Sicht auf" is active (ticket 2610052). */
  get activeUser() {
    return getEffectiveUser();
  }

  get isAllowedToViewLicencesOfGroup() {
    return licenceAdministrationAccess();
  }

  get memberClientIds() {
    return getMemberClientIds();
  }

  /** Table header background: light grey in light mode, dark elevated surface in dark mode. */
  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  get sortOrderMenuIconRes() {
    return "mdi-sort-alphabetical-variant";
  }

  get roleFilterIconRes() {
    return "mdi-filter-variant";
  }

  getSortOrderName(sortOrder: UserSortOrder) {
    switch (sortOrder) {
      case UserSortOrder.NICKNAME:
        return this.$t("users.nickname");
      case UserSortOrder.FIRST_NAME:
        return this.$t("users.firstName");
      case UserSortOrder.LAST_NAME:
        return this.$t("users.lastName");
    }
  }

  getRoleFilterName(role: Role | typeof ROLE_FILTER_ALL) {
    if (role === ROLE_FILTER_ALL) {
      return this.$t("users.allRoles");
    }

    return getRoleLabel(role, this);
  }

  getRoleFilterKey(role: Role | typeof ROLE_FILTER_ALL) {
    return role.toString();
  }

  async onOpenLicenceUsageDetails() {
    if (this.licenceInfoV3) {
      await this.licenceUsageDetails.show(this.licenceInfoV3, this.licenceInfoV3OfGroup, this.numberOfMemberClientsForLicenceInfoV3);
    }
  }

  isAllowedToEdit(user: User) {
    const activeUser = this.activeUser;

    if (
      !editAdminAccess(activeUser) &&
      adminRightsAccess(user)
    ) {
      return false;
    }
    else if (
      !editUsersAccess(activeUser) &&
      !adminRightsAccess(user)
    ) {
      return false;
    }

    return true;
  }

  get sortedUsers() {
    const searchQuery = this.searchQuery.trim();
    const searchQueryUpper = searchQuery.toUpperCase();

    const branchFilter = this.selectedBranchFilter;
    const selectedRole = this.selectedRoleFilter;

    return UserModule.users
      .filter((user) => user.id !== "IRES_ADMIN_ACCOUNT_ID" && !user.deleted)
      .filter((user) => {
        if (!branchFilter?.branch) {
          return true;
        }
        return doesUserBelongToBranch(user, branchFilter.branch.id, true);
      })
      .filter((user) => {
        if (selectedRole === ROLE_FILTER_ALL) {
          return true;
        }

        return user.role === selectedRole;
      })
      .filter((user) => {
        if (!searchQuery) {
          return true;
        }

        const nickname = (user.nickname ?? "").toUpperCase();
        const staffNumber = (user.staffNumber ?? "").toUpperCase();
        const firstName = (user.firstName ?? "").toUpperCase();
        const lastName = (user.lastName ?? "").toUpperCase();
        const fullName = `${firstName} ${lastName}`.trim();
        const emailAddress = (user.emailAddress ?? "").toUpperCase();

        return (
          nickname.includes(searchQueryUpper) ||
          staffNumber.includes(searchQueryUpper) ||
          firstName.includes(searchQueryUpper) ||
          lastName.includes(searchQueryUpper) ||
          fullName.includes(searchQueryUpper) ||
          emailAddress.includes(searchQueryUpper) ||
          user.id === searchQuery
        );
      })
      .sort((u1, u2) => {
        const value1 = this.getSortValue(u1);
        const value2 = this.getSortValue(u2);

        const primary = value1.localeCompare(value2);
        if (primary !== 0) {
          return primary;
        }

        return (u1.nickname ?? "").localeCompare(u2.nickname ?? "");
      });
  }

  get usersActiveFiltersName() {
    const roleFilterName = this.getRoleFilterName(this.selectedRoleFilter).toString();
    const filterNames = [roleFilterName];

    if (this.isBranchFilterIconShown) {
      const branchFilterName = this.selectedBranchFilter.branch
        ? getBranchName(this.selectedBranchFilter.branch)
        : this.$t("common.all").toString();
      filterNames.push(branchFilterName);
    }

    const searchQuery = this.searchQuery.trim();
    if (searchQuery) {
      filterNames.push(`${this.$t("common.search").toString()}: ${searchQuery}`);
    }

    return filterNames.join(", ");
  }

  get isBranchFilterIconShown() {
    return getBranchFiltersOfUser(this.activeUser).length > 0;
  }

  getSortValue(user: User) {
    switch (this.selectedSortOrder) {
      case UserSortOrder.FIRST_NAME:
        return (user.firstName ?? "").toLowerCase();
      case UserSortOrder.LAST_NAME:
        return (user.lastName ?? "").toLowerCase();
      case UserSortOrder.NICKNAME:
      default:
        return (user.nickname ?? "").toLowerCase();
    }
  }

  debounceSearch(newQuery: string) {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    if (newQuery === "") {
      this.clearSearchQuery();
      return;
    }

    this.searchTimeout = setTimeout(() => {
      this.searchQuery = newQuery;
    }, 400);
  }

  clearSearchQuery() {
    this.searchQuery = "";
  }

  unlockSearchInput() {
    this.isSearchInputReadonly = false;
  }

  /** Hover wins over the last-opened tint, so the row under the pointer always responds. */
  rowStyle(user: User, hover: boolean): string {
    const hoverBackground = hover ? themed("", COLOR_LIST_ROW_HOVER_DARK) : "";

    if (hoverBackground) {
      return `background-color: ${hoverBackground}`;
    }

    return userListLastVisitedKey(user.id) === this.lastVisitedKey
      ? LAST_VISITED_ITEM_BACKGROUND_STYLE
      : "";
  }

  onUserOpened(user: User) {
    this.lastVisitedKey = userListLastVisitedKey(user.id);
    setLastVisitedItem(this.lastVisitedKey, StorageKeyLastVisitedItemLevel.LEVEL_1);
  }

  /**
   * Back on the list, bring the user opened last into view. Deferred by one macrotask so it runs
   * after the router's own scroll to the top.
   */
  async scrollToLastVisitedUser() {
    if (!this.lastVisitedKey) {
      return;
    }

    await this.$nextTick();
    await new Promise((resolve) => setTimeout(resolve, 0));

    const row = Array.from(this.$el.querySelectorAll<HTMLElement>("[data-user-id]")).find(
      (element) => userListLastVisitedKey(element.dataset.userId ?? "") === this.lastVisitedKey
    );

    row?.scrollIntoView({ block: "center" });
  }

  onUserDialogClosed() {
    this.focusSearchFieldIfNeeded();
  }

  focusSearchFieldIfNeeded() {
    if (!this.searchQuery) {
      return;
    }

    this.$nextTick(() => {
      const isMobile = this.$vuetify.breakpoint.smAndDown;
      const field = isMobile ? this.searchFieldMobile : this.searchFieldDesktop;

      if (!field) {
        return;
      }

      const fieldAny = field as unknown as { focus?: () => void; $el?: HTMLElement };
      if (fieldAny.focus) {
        fieldAny.focus();
      }

      const input = fieldAny.$el?.querySelector("input") as HTMLInputElement | null;
      if (input) {
        input.focus();
        input.select();
      }
    });
  }

  get canCreateUser() {
    const activeUser = UserModule.activeUser;
    if (!activeUser) {
      return false;
    }
    return createUserAccess(activeUser);
  }

  async mounted() {
    this.setToolbarTitle();
    this.scrollToLastVisitedUser();
    
    this.subscriptions.push(
      observeLicenceInfoForClient((licenceInfo) => {
        this.licenceInfoV3 = licenceInfo.licenceDetailsV3 ?? null;
      })
    );

    if (this.isAllowedToViewLicencesOfGroup) {
      this.numberOfMemberClientsForLicenceInfoV3 = 0;
      const [ licenceInfoOfMemberClients, clientsInfoOfGroup ] = await Promise.all([
        getLicenceInfoOfMemberClients(this.memberClientIds),
        getMemberClientsInformationOfGroup()
      ]);
      
      const activeMemberClientIds = Array.from(clientsInfoOfGroup.keys());

      licenceInfoOfMemberClients.forEach(({ clientId, licenceInfo }) => {
        if (
          licenceInfo.licenceDetailsV3 && 
          clientId !== getClientId() &&
          activeMemberClientIds.includes(clientId)
        ) {
          this.numberOfMemberClientsForLicenceInfoV3++;
          this.licenceInfoV3OfGroup.push(licenceInfo.licenceDetailsV3);
        }
      })
    }
  }

  beforeDestroy() {
    this.subscriptions.forEach((subscription) => subscription());
  }

  setToolbarTitle() {
    const baseTitle = this.$t("pages.settings").toString();
    const currentPageTitle = this.$t("settings.users").toString();

    const toolbarTitle = baseTitle + " > " + currentPageTitle;

    ToolbarContentModule.setToolbarTitle(toolbarTitle);
  }
}
